import { useEffect, useMemo, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ShoppingCart,
  User,
  LogOut,
  Moon,
  Sun,
  Menu,
  Search,
  Package,
  Bell,
  Heart,
  MapPin,
  MessageSquare,
  MessageCircle,
  Navigation,
  Loader2,
  Share2,
  Gift,
  Truck,
  ChevronDown,
  Settings,
  Store,
  ArrowLeftRight,
} from "lucide-react";
import { StoopMark } from "@/components/icons/StoopMark";
import ActiveOrderBanner from "@/components/ActiveOrderBanner";
import { ComplianceAttestationBanner } from "@/components/ComplianceAttestationBanner";
import { useAuth } from "@/hooks/useAuth";
import { clearStoredReferralCode } from "@/lib/referral";
import { tierStyle } from "@/lib/tierStyle";
import { useTheme } from "@/components/ThemeProvider";
import { recordingModeActive } from "@/lib/recordingMode";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  type AppMode,
  getAllowedModes,
  getDefaultRouteForMode,
  persistMode,
} from "@/lib/mode";
import { useCart } from "@/hooks/useCart";

type HeaderProps = {
  cartCount?: number;
  onCartClick?: () => void;
  onCollapseChange?: (collapsed: boolean) => void;
  collapsed?: boolean;
  searchQuery?: string;
  onSearchQueryChange?: (value: string) => void;
  locationLabel?: string;
  onUseCurrentLocation?: () => void;
  isLocating?: boolean;
  locationError?: string | null;
};

export default function Header({
  cartCount = 0,
  onCartClick,
  onCollapseChange,
  collapsed,
  searchQuery,
  onSearchQueryChange,
  locationLabel,
  onUseCurrentLocation,
  isLocating,
  locationError,
}: HeaderProps) {
  const { isAuthenticated, user } = useAuth();
  const serverIsDemoAdmin = Boolean((user as any)?.isDemoAdmin);
  const userId = user?.id ? String(user.id) : null;
  // Remember admin status per-user so the Beta Feedback menu item doesn't
  // disappear on transient auth hiccups or stale WebView JS.
  const [rememberedAdmin, setRememberedAdmin] = useState(() => {
    if (typeof window === "undefined" || !userId) return false;
    return window.localStorage.getItem(`marketlink.admin.${userId}`) === "1";
  });
  useEffect(() => {
    if (!userId) return;
    if (serverIsDemoAdmin) {
      window.localStorage.setItem(`marketlink.admin.${userId}`, "1");
      setRememberedAdmin(true);
    } else {
      const cached = window.localStorage.getItem(`marketlink.admin.${userId}`) === "1";
      setRememberedAdmin(cached);
    }
  }, [userId, serverIsDemoAdmin]);
  const isDemoAdmin =
    (serverIsDemoAdmin || (isAuthenticated && rememberedAdmin)) && !recordingModeActive();
  const { theme, setTheme } = useTheme();
  const [location, setLocation] = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLocationPopoverOpen, setIsLocationPopoverOpen] = useState(false);
  const [referralExpanded, setReferralExpanded] = useState(false);
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const [headerDebug, setHeaderDebug] = useState(false);
  const [debugScrollY, setDebugScrollY] = useState(0);
  const isCollapsed = collapsed ?? internalCollapsed;

  const allowedModes = getAllowedModes(user?.roles);
  const canSell = allowedModes.includes("seller");
  const currentMode: AppMode = useMemo(() => {
    const onSellerRoute = /^\/seller($|[/?])/.test(location);
    if (onSellerRoute && allowedModes.includes("seller")) return "seller";
    if (allowedModes.includes("buyer")) return "buyer";
    return allowedModes[0] ?? "buyer";
  }, [location, allowedModes]);

  useEffect(() => {
    if (!isAuthenticated) return;
    persistMode(currentMode);
  }, [currentMode, isAuthenticated]);

  // Cart is always visible when authenticated, regardless of mode or page.
  // Sellers can be in seller mode and still get to whatever they were
  // buying; an empty cart shows with badge 0 and the click sends them to
  // browse. Pages that wire their own cart sheet (Home/HomeFeed/Profile/
  // ProductDetail) keep that sheet via onCartClick.
  const { cart } = useCart();
  const [, navigate] = useLocation();
  const cartHasItems = cart.length > 0;
  const showCartButton = isAuthenticated;
  const handleCartClick = () => {
    if (onCartClick) {
      onCartClick();
      return;
    }
    if (cartHasItems) {
      navigate("/checkout");
    } else {
      navigate("/?browse=boxes");
    }
  };
  const headerCartCount = cartCount || cart.reduce((sum, item) => sum + (item.qty ?? 0), 0);

  const { data: unreadNotificationCount = 0 } = useQuery<number>({
    queryKey: ["/api/my/notifications/unread-count"],
    enabled: isAuthenticated,
    queryFn: async () => {
      const res = await fetch("/api/my/notifications/unread-count", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      return Number(data?.count || 0);
    },
  });

  const { data: unreadMessageCount = 0 } = useQuery<number>({
    queryKey: ["/api/my/unread-messages"],
    enabled: isAuthenticated,
    refetchInterval: 30000,
    queryFn: async () => {
      const res = await fetch("/api/my/unread-messages", { credentials: "include" });
      if (!res.ok) return 0;
      const data = await res.json();
      return Number(data?.count || 0);
    },
  });

  const totalUnreadBadge = unreadNotificationCount + unreadMessageCount;

  type Reward = { id: string; label: string; discountPercent: number; type: string };
  const { data: availableRewards = [] } = useQuery<Reward[]>({
    queryKey: ["/api/my/rewards"],
    enabled: isAuthenticated,
    queryFn: async () => {
      const res = await fetch("/api/my/rewards", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 60_000,
  });

  type ReferralStats = {
    totalReferred: number;
    canSell: boolean;
    featuredThreshold: number;
    isFeatured: boolean;
    featuredUntil: string | null;
    referralsUntilFeatured: number | null;
    tier: string | null;
    tierDiscount: number | null;
    nextTier: string | null;
    nextTierDiscount: number | null;
    nextTierReferralsAway: number | null;
    foundingChampionCap: number | null;
    foundingChampionSpotsRemaining: number | null;
  };
  const { data: newFeedbackCount = 0 } = useQuery<number>({
    queryKey: ["/api/admin/feedback", "new-count"],
    enabled: isAuthenticated && Boolean((user as any)?.isDemoAdmin),
    queryFn: async () => {
      const res = await fetch("/api/admin/feedback?status=new", { credentials: "include" });
      if (!res.ok) return 0;
      const rows = await res.json();
      return Array.isArray(rows) ? rows.length : 0;
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const { data: referralStats } = useQuery<ReferralStats>({
    queryKey: ["/api/my/referral-stats"],
    enabled: isAuthenticated,
    queryFn: async () => {
      const res = await fetch("/api/my/referral-stats", { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    staleTime: 60_000,
  });

  type Inviter = { id: string; displayName: string | null; firstName: string | null; count: number; tier: string };
  const { data: topInviters = [] } = useQuery<Inviter[]>({
    queryKey: ["/api/leaderboard/top-inviters"],
    enabled: isAuthenticated,
    queryFn: async () => {
      const res = await fetch("/api/leaderboard/top-inviters", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const debugFromStorage = window.localStorage.getItem("marketlink.headerDebug") === "1";
    const debugFromQuery = new URLSearchParams(window.location.search).get("debugHeader") === "1";
    const nextHeaderDebug = debugFromStorage || debugFromQuery;

    if (debugFromQuery) {
      window.localStorage.setItem("marketlink.headerDebug", "1");
    }

    setHeaderDebug(nextHeaderDebug);

    if (collapsed !== undefined) return;

    const EXPAND_Y = 12;
    const COLLAPSE_Y = 36;
    let ticking = false;

    const updateScrollState = () => {
      const shell = document.getElementById("app-scroll");
      const y = Math.max((shell ? shell.scrollTop : window.scrollY) || 0, 0);
      setDebugScrollY(y);

      setInternalCollapsed((prev) => {
        let next = prev;

        if (!prev && y >= COLLAPSE_Y) {
          next = true;
        } else if (prev && y <= EXPAND_Y) {
          next = false;
        }

        if (next !== prev) {
          onCollapseChange?.(next);
        }

        return next;
      });

      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateScrollState);
    };

    updateScrollState();
    // Capture on document: scroll events from the app shell don't bubble, so a
    // window listener alone stopped firing once the document stopped scrolling.
    document.addEventListener("scroll", onScroll, { passive: true, capture: true });
    window.addEventListener("resize", onScroll);

    return () => {
      document.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", onScroll);
    };
  }, [collapsed, onCollapseChange]);

  useEffect(() => {
    if (!isLocationPopoverOpen) return;
    const close = () => setIsLocationPopoverOpen(false);
    // Capture, not just window: under the swipe pager the document never
    // scrolls, so a window listener alone never fires and the portalled popover
    // stays pinned mid-screen while this row slides away beneath it.
    window.addEventListener("scroll", close, { passive: true });
    document.addEventListener("scroll", close, { passive: true, capture: true });
    return () => {
      window.removeEventListener("scroll", close);
      document.removeEventListener("scroll", close, { capture: true } as EventListenerOptions);
    };
  }, [isLocationPopoverOpen]);

  const handleModeChange = (mode: AppMode) => {
    if (!allowedModes.includes(mode)) return;
    persistMode(mode);
    setLocation(getDefaultRouteForMode(mode));
    setIsMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      const res = await fetch("/api/logout", {
        method: "POST",
        credentials: "include",
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Logout failed with status ${res.status}`);
      }

      window.localStorage.removeItem("marketlink.mode");
      if (userId) {
        window.localStorage.removeItem(`marketlink.admin.${userId}`);
      }
      clearStoredReferralCode();
      window.location.assign("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const resolvedTheme =
    theme === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : theme;

  const toggleTheme = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  };

  const closeMenu = () => setIsMenuOpen(false);
  const showDesktopActions = !isCollapsed;
  const showDesktopHamburger = isCollapsed;
  const showMobileAuthTextButtons =
    !isAuthenticated &&
    (location === "/");

  const showSearchControls =
    typeof searchQuery === "string" &&
    typeof onSearchQueryChange === "function";

  const mainRowHeight = 54;
  const mobileTopRowHeight = 44;

  // Native reports the installed binary (version + build); the browser only has
  // the bundle version baked in at build time. Resolved async because
  // App.getInfo() is a bridge call and isn't available on the web at all.
  // Null on the web: VITE_APP_VERSION is never actually set at build time, so
  // there is no honest version to show there — better blank than "web".
  const [appBuildLabel, setAppBuildLabel] = useState<string | null>(
    (import.meta as any).env?.VITE_APP_VERSION
      ? `Stoop ${(import.meta as any).env.VITE_APP_VERSION}`
      : null,
  );
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;
    void CapApp.getInfo()
      .then((info) => {
        if (!cancelled) setAppBuildLabel(`Stoop ${info.version} (${info.build})`);
      })
      .catch(() => {
        /* leave the web fallback in place */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const MenuPanel = (
    <>
      <DialogHeader className="border-b border-[rgba(125,95,52,0.18)] bg-drawer-header-gradient px-5 py-4 text-left dark:border-border/50 dark:bg-none dark:bg-card">
        <DialogTitle>Menu</DialogTitle>
        <DialogDescription>
          Browse, search, and access your account tools.
        </DialogDescription>
      </DialogHeader>

      <div className="flex h-full flex-col gap-5 overflow-y-auto bg-drawer-gradient px-5 py-5 dark:bg-none dark:bg-background">
        {showSearchControls && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground">Find local food</p>

            <div className="space-y-3 rounded-xl border border-[rgba(125,95,52,0.14)] bg-white/60 p-3 dark:border-border/40 dark:bg-card/40">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => onSearchQueryChange?.(e.target.value)}
                  placeholder="Search local products"
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        )}

        {locationLabel !== undefined && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground">Your location</p>
            <div className="space-y-2 rounded-xl border border-[rgba(125,95,52,0.14)] bg-white/60 p-3 dark:border-border/40 dark:bg-card/40">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-semibold">{locationLabel}</span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full justify-start gap-2 text-xs"
                onClick={onUseCurrentLocation}
                disabled={isLocating}
              >
                {isLocating ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Navigation className="h-3.5 w-3.5" />
                )}
                {isLocating ? "Locating..." : "Use current location"}
              </Button>
              {locationError && (
                <p className="text-[11px] text-amber-600">{locationError}</p>
              )}
            </div>
          </div>
        )}

        {isAuthenticated && allowedModes.length > 1 && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground">Mode</p>

            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={currentMode === "buyer" ? "default" : "outline"}
                onClick={() => handleModeChange("buyer")}
              >
                Buy
              </Button>
              <Button
                type="button"
                variant={currentMode === "seller" ? "default" : "outline"}
                onClick={() => handleModeChange("seller")}
              >
                Sell
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <p className="text-sm font-medium text-muted-foreground">Quick actions</p>

          <div className="grid gap-2">
            <Button
              type="button"
              variant="outline"
              className="justify-start bg-white/60 dark:bg-card/40"
              onClick={() => {
                toggleTheme();
                closeMenu();
              }}
            >
              {resolvedTheme === "dark" ? (
                <>
                  <Sun className="mr-2 h-4 w-4" />
                  Light mode
                </>
              ) : (
                <>
                  <Moon className="mr-2 h-4 w-4" />
                  Dark mode
                </>
              )}
            </Button>

            {isAuthenticated ? (
              <>
                {showCartButton && (
                  <Button
                    type="button"
                    variant="outline"
                    className="justify-start bg-white/60 dark:bg-card/40"
                    onClick={() => {
                      handleCartClick();
                      closeMenu();
                    }}
                  >
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    Cart ({headerCartCount})
                  </Button>
                )}

                {currentMode === "buyer" && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      className="justify-start bg-white/60 dark:bg-card/40"
                      asChild
                    >
                      <Link href="/?browse=saved" onClick={closeMenu}>
                        <Heart className="mr-2 h-4 w-4" />
                        Saved
                      </Link>
                    </Button>

                  </>
                )}

                {canSell && (
                  <Button
                    type="button"
                    variant="outline"
                    className="justify-start bg-white/60 dark:bg-card/40"
                    asChild
                  >
                    <Link href="/seller" onClick={closeMenu}>
                      <Store className="mr-2 h-4 w-4" />
                      Seller dashboard
                    </Link>
                  </Button>
                )}

                <Button
                  type="button"
                  variant="outline"
                  className="justify-start bg-white/60 dark:bg-card/40"
                  asChild
                >
                  <Link href="/deliveries" onClick={closeMenu}>
                    <Truck className="mr-2 h-4 w-4" />
                    Deliveries
                  </Link>
                </Button>

                {user?.canBarter && (
                  <Button
                    type="button"
                    variant="outline"
                    className="justify-start bg-white/60 dark:bg-card/40"
                    asChild
                  >
                    <Link href="/barter" onClick={closeMenu}>
                      <ArrowLeftRight className="mr-2 h-4 w-4" />
                      Trades
                    </Link>
                  </Button>
                )}

                <Button
                  type="button"
                  variant="outline"
                  className="justify-start bg-white/60 dark:bg-card/40"
                  onClick={async () => {
                    try {
                      const res = await fetch("/api/my/referral-code", { credentials: "include" });
                      if (!res.ok) throw new Error();
                      const data = await res.json() as { code: string; shareUrl: string };
                      const shareText = `Join me on Stoop — local food from the makers on your block! ${data.shareUrl}`;
                      if (navigator.share) {
                        await navigator.share({ title: "Stoop", text: shareText });
                      } else {
                        await navigator.clipboard.writeText(data.shareUrl);
                        alert("Link copied!");
                      }
                    } catch {
                      /* user cancelled share or clipboard failed */
                    }
                  }}
                >
                  <Share2 className="mr-2 h-4 w-4" />
                  Share & Invite
                </Button>

                {(availableRewards.length > 0 || (referralStats?.canSell && (referralStats.totalReferred ?? 0) > 0) || (referralStats?.tier && (referralStats.totalReferred ?? 0) > 0)) && (
                  <div className="rounded-lg border border-primary/20 bg-primary/5">
                    <button
                      type="button"
                      onClick={() => setReferralExpanded((v) => !v)}
                      className="flex w-full items-center justify-between gap-2 p-3 text-left"
                      aria-expanded={referralExpanded}
                      data-testid="referral-panel-toggle"
                    >
                      <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-primary">
                        <Gift className="h-4 w-4 shrink-0" />
                        <span className="truncate">
                          {availableRewards.length > 0
                            ? `${availableRewards.length} reward${availableRewards.length === 1 ? "" : "s"} available`
                            : referralStats?.tier
                              ? `${referralStats.tier} · ${referralStats.totalReferred} invited`
                              : "Rewards & invites"}
                        </span>
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 shrink-0 text-primary transition-transform ${referralExpanded ? "rotate-180" : ""}`}
                      />
                    </button>
                    {referralExpanded && (
                    <div className="space-y-2 border-t border-primary/10 p-3">
                    {availableRewards.length > 0 && (
                      <>
                        <ul className="space-y-1">
                          {availableRewards.slice(0, 3).map((r) => (
                            <li key={r.id} className="text-[11px] text-muted-foreground">
                              {r.label}
                            </li>
                          ))}
                        </ul>
                        <div className="text-[10px] italic text-muted-foreground">
                          One reward per order — applied automatically at checkout.
                        </div>
                      </>
                    )}
                    {referralStats?.tier && (referralStats.totalReferred ?? 0) > 0 && (
                      <div className="border-t border-primary/10 pt-2 text-[11px]">
                        <div className="flex items-center gap-2">
                          {(() => {
                            const style = tierStyle(referralStats.tier);
                            return (
                              <span
                                className={`inline-flex items-center rounded-full border px-2 py-0.5 font-semibold ${style.className}`}
                              >
                                {style.icon} {referralStats.tier}
                              </span>
                            );
                          })()}
                          <span className="text-muted-foreground">
                            {referralStats.totalReferred} {referralStats.totalReferred === 1 ? "neighbor" : "neighbors"} invited
                          </span>
                        </div>
                        {referralStats.nextTier && referralStats.nextTierReferralsAway != null && referralStats.nextTierReferralsAway > 0 && (
                          <div className="mt-1.5 text-muted-foreground">
                            Invite{" "}
                            <span className="font-medium text-foreground">{referralStats.nextTierReferralsAway}</span>{" "}
                            more to reach{" "}
                            <span className="font-semibold text-primary">{referralStats.nextTier}</span>{" "}
                            — every reward bumps to{" "}
                            <span className="font-semibold text-primary">{referralStats.nextTierDiscount}% off</span>
                          </div>
                        )}
                        {!referralStats.nextTier && (
                          <div className="mt-1 text-muted-foreground">You've maxed every tier — thanks for spreading the word.</div>
                        )}
                        {referralStats.nextTier !== "Founding Champion" &&
                          referralStats.foundingChampionSpotsRemaining != null &&
                          referralStats.foundingChampionSpotsRemaining > 0 && (
                            <div className="mt-1.5 rounded-md bg-rose-50 px-2 py-1 text-[10px] text-rose-800 dark:bg-rose-950/30 dark:text-rose-300">
                              ♛ Refer 50 → become a <span className="font-semibold">Founding Champion</span> · up to $200 off ·{" "}
                              <span className="font-semibold">{referralStats.foundingChampionSpotsRemaining}</span> of {referralStats.foundingChampionCap} spots left
                            </div>
                          )}
                      </div>
                    )}
                    {topInviters.length > 0 && (
                      <div className="border-t border-primary/10 pt-2 text-[11px]">
                        <div className="mb-1 font-semibold text-foreground">Top inviters</div>
                        <ol className="space-y-0.5">
                          {topInviters.slice(0, 5).map((inv, idx) => {
                            const name = inv.displayName || inv.firstName || "A neighbor";
                            const isYou = inv.id === user?.id;
                            return (
                              <li
                                key={inv.id}
                                className="flex items-center justify-between gap-2 text-muted-foreground"
                              >
                                <span className="truncate">
                                  <span className="mr-1 inline-block w-4 text-right tabular-nums text-foreground">
                                    {idx + 1}.
                                  </span>
                                  <span className={isYou ? "font-semibold text-foreground" : ""}>
                                    {name}
                                    {isYou ? " (you)" : ""}
                                  </span>
                                  <span className="ml-1.5 text-[10px] text-muted-foreground">
                                    · {inv.tier}
                                  </span>
                                </span>
                                <span className="tabular-nums text-foreground">{inv.count}</span>
                              </li>
                            );
                          })}
                        </ol>
                      </div>
                    )}
                    {referralStats?.canSell && referralStats.isFeatured && (
                      <div className="flex items-center gap-2 text-[11px]">
                        <span className="inline-flex items-center rounded-full border border-amber-300/40 bg-amber-50 px-2 py-0.5 font-semibold text-amber-700 dark:border-amber-600/30 dark:bg-amber-950/40 dark:text-amber-400">
                          ★ Featured Seller
                        </span>
                        <span className="text-muted-foreground">
                          {referralStats.featuredUntil
                            ? `until ${new Date(referralStats.featuredUntil).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
                            : ""}
                        </span>
                      </div>
                    )}
                    {referralStats?.canSell && !referralStats.isFeatured && referralStats.referralsUntilFeatured != null && referralStats.referralsUntilFeatured > 0 && (
                      <div className="text-[11px] text-muted-foreground">
                        <span className="font-medium text-foreground">{referralStats.totalReferred}</span> of{" "}
                        <span className="font-medium text-foreground">{referralStats.featuredThreshold}</span> referrals
                        to earn <span className="font-semibold text-amber-700 dark:text-amber-400">★ Featured Seller</span>
                        <div className="mt-1 h-1.5 w-full rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full rounded-full bg-amber-500 transition-all"
                            style={{ width: `${Math.min(100, (referralStats.totalReferred / referralStats.featuredThreshold) * 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                    </div>
                    )}
                  </div>
                )}

                {isDemoAdmin && (
                  <Button
                    type="button"
                    variant="outline"
                    className="justify-start bg-white/60 dark:bg-card/40"
                    asChild
                  >
                    <Link
                      href="/admin/feedback"
                      onClick={closeMenu}
                      className="flex w-full items-center justify-between"
                    >
                      <span className="inline-flex items-center">
                        <MessageSquare className="mr-2 h-4 w-4" />
                        Beta feedback
                      </span>
                      {newFeedbackCount > 0 && (
                        <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                          {newFeedbackCount > 9 ? "9+" : newFeedbackCount}
                        </span>
                      )}
                    </Link>
                  </Button>
                )}

                {isDemoAdmin && (
                  <Button
                    type="button"
                    variant="outline"
                    className="justify-start bg-white/60 dark:bg-card/40"
                    asChild
                  >
                    <Link
                      href="/admin/demo-control"
                      onClick={closeMenu}
                      className="flex w-full items-center"
                    >
                      <Truck className="mr-2 h-4 w-4" />
                      Demo control
                    </Link>
                  </Button>
                )}

                <Button
                  type="button"
                  variant="outline"
                  className="justify-start bg-white/60 dark:bg-card/40"
                  asChild
                >
                  <Link href="/account" onClick={closeMenu}>
                    <Settings className="mr-2 h-4 w-4" />
                    Account settings
                  </Link>
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="justify-start bg-white/60 dark:bg-card/40"
                  onClick={handleLogout}
                  aria-label="Logout"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </Button>

                {user?.email && (
                  <div className="pt-1 text-center">
                    <p className="text-[11px] text-muted-foreground">
                      Signed in as <span className="font-medium">{user.email}</span>
                      {isDemoAdmin && (
                        <span className="ml-1 inline-flex items-center rounded-full bg-primary/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-primary">
                          Admin
                        </span>
                      )}
                    </p>
                    {(user as any)?.demoAdminCheck && (
                      <p className="mt-0.5 text-[9px] text-muted-foreground/70 font-mono">
                        isDemoAdmin={String((user as any).isDemoAdmin)} · match={String((user as any).demoAdminCheck.match)} · demo={String((user as any).demoAdminCheck.demoEnabled)} · adminLen={(user as any).demoAdminCheck.adminEmailLength}
                      </p>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="justify-start bg-white/60 dark:bg-card/40"
                  asChild
                >
                  <Link href="/login" onClick={closeMenu}>
                    Login
                  </Link>
                </Button>

                <Button type="button" className="justify-start" asChild>
                  <Link href="/register" onClick={closeMenu}>
                    Sign Up
                  </Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Which build you're actually on. Nothing surfaced this before, so a
          tester juggling a Play release and a Firebase build had no way to tell
          them apart, and a feedback report couldn't say either. Native shows the
          binary's build number; the web shows the bundle version. */}
      {appBuildLabel && (
        <div className="px-5 pb-5 pt-1 text-[11px] text-muted-foreground">
          {appBuildLabel}
        </div>
      )}
    </>
  );

  return (
    <>
      {headerDebug && (
        <div className="fixed bottom-3 right-3 z-[100] rounded-md border border-black/10 bg-black/80 px-3 py-2 text-xs text-white shadow-lg">
          <div>scrollY: {Math.round(debugScrollY)}</div>
          <div>collapsed: {String(isCollapsed)}</div>
          <div>{"expand<=12 collapse>=36"}</div>
          <button
            type="button"
            className="mt-2 rounded border border-white/20 px-2 py-1 text-[11px]"
            onClick={() => {
              window.localStorage.removeItem("marketlink.headerDebug");
              setHeaderDebug(false);
            }}
          >
            Hide debug
          </button>
        </div>
      )}

      <header
        className="sticky top-0 z-50 md:border-b border-primary/90 bg-brand-gradient text-white shadow-brand dark:bg-none dark:bg-background dark:text-foreground"
        style={{ paddingTop: "var(--safe-area-top)" }}
      >
      <div className="container mx-auto px-4 min-w-0">
        <div
          className="flex items-center justify-between gap-3 overflow-hidden"
          style={{ height: `${mainRowHeight}px` }}
        >
          <Link href="/" className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
            <StoopMark className="h-7 w-7 shrink-0 text-white dark:text-primary" />
            <span
              className="truncate font-display font-black tracking-wider text-white drop-shadow-brand dark:text-foreground"
              style={{
                fontSize: "clamp(1.85rem, 5.8vw, 2.625rem)",
                fontVariationSettings: "'opsz' 144",
                lineHeight: 1,
              }}
            >
              stoop
            </span>
          </Link>

          <div className="hidden shrink-0 items-center gap-2 lg:gap-3 xl:flex">
            {locationLabel !== undefined && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex min-w-0 items-center gap-1 rounded-full border border-white/25 bg-white/10 px-2.5 py-1 text-xs font-medium text-white/90 hover:bg-white/20 dark:border-border dark:bg-background dark:text-foreground"
                  >
                    <MapPin className="h-3 w-3 shrink-0" />
                    <span className="truncate max-w-[120px]">{locationLabel}</span>
                    <span className="ml-0.5 text-[10px] opacity-60">▾</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-56 p-3">
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Your location</p>
                    <p className="text-sm font-semibold">{locationLabel}</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full justify-start gap-2 text-xs"
                      onClick={onUseCurrentLocation}
                      disabled={isLocating}
                    >
                      {isLocating ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Navigation className="h-3.5 w-3.5" />
                      )}
                      {isLocating ? "Locating..." : "Use current location"}
                    </Button>
                    {locationError && (
                      <p className="text-[11px] text-amber-600">{locationError}</p>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            )}
            {showDesktopActions && (
              <>
                {isAuthenticated && allowedModes.length > 1 && (
                  <div className="flex items-center rounded-full border border-white/30 bg-white/10 p-1 dark:border-border dark:bg-background">
                    <button
                      type="button"
                      onClick={() => handleModeChange("buyer")}
                      className={`rounded-full px-3 py-1 text-sm font-medium transition ${
                        currentMode === "buyer"
                          ? "bg-primary text-primary-foreground"
                          : "text-white/80 dark:text-muted-foreground"
                      }`}
                    >
                      Buy
                    </button>
                    <button
                      type="button"
                      onClick={() => handleModeChange("seller")}
                      className={`rounded-full px-3 py-1 text-sm font-medium transition ${
                        currentMode === "seller"
                          ? "bg-primary text-primary-foreground"
                          : "text-white/80 dark:text-muted-foreground"
                      }`}
                    >
                      Sell
                    </button>
                  </div>
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  className="px-3 text-white hover:bg-white/10 hover:text-white dark:text-foreground dark:hover:bg-accent"
                  onClick={toggleTheme}
                >
                  {resolvedTheme === "dark" ? (
                    <>
                      <Sun className="mr-2 h-4 w-4" />
                      <span>Light</span>
                    </>
                  ) : (
                    <>
                      <Moon className="mr-2 h-4 w-4" />
                      <span>Dark</span>
                    </>
                  )}
                </Button>

                {isAuthenticated ? (
                  <>
                    {showCartButton && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="px-3 text-white hover:bg-white/10 hover:text-white dark:text-foreground dark:hover:bg-accent"
                        onClick={handleCartClick}
                      >
                        <ShoppingCart className="mr-2 h-4 w-4" />
                        <span>Cart ({headerCartCount})</span>
                      </Button>
                    )}

                    {currentMode === "buyer" && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="px-3 text-white hover:bg-white/10 hover:text-white dark:text-foreground dark:hover:bg-accent"
                          asChild
                        >
                          <Link href="/orders">My Orders</Link>
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          className="px-3 text-white hover:bg-white/10 hover:text-white dark:text-foreground dark:hover:bg-accent"
                          asChild
                        >
                          <Link href="/notifications" className="inline-flex items-center gap-2">
                            <span>Notifications</span>
                            {unreadNotificationCount > 0 && (
                              <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
                                {unreadNotificationCount > 9 ? "9+" : unreadNotificationCount}
                              </span>
                            )}
                          </Link>
                        </Button>
                      </>
                    )}

                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-3 text-white hover:bg-white/10 hover:text-white dark:text-foreground dark:hover:bg-accent"
                      asChild
                    >
                      <Link href="/account" aria-label="Account">
                        <User className="h-4 w-4" />
                      </Link>
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-3 text-white hover:bg-white/10 hover:text-white dark:text-foreground dark:hover:bg-accent"
                      onClick={handleLogout}
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Logout</span>
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-3 text-white hover:bg-white/10 hover:text-white dark:text-foreground dark:hover:bg-accent"
                      asChild
                    >
                      <Link href="/login">Login</Link>
                    </Button>
                    <Button size="sm" className="px-3" asChild>
                      <Link href="/register">Sign Up</Link>
                    </Button>
                  </>
                )}
              </>
            )}

            {showDesktopHamburger && (
              <Dialog open={isMenuOpen} onOpenChange={setIsMenuOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative overflow-visible text-white hover:bg-white/10 hover:text-white dark:text-foreground dark:hover:bg-accent"
                    aria-label="Open menu"
                  >
                    <Menu className="h-5 w-5 translate-y-0.5" />
                    {totalUnreadBadge > 0 && (
                      <span className="absolute left-2 top-1 z-10 inline-flex min-w-[1rem] h-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white shadow-sm">
                        {totalUnreadBadge > 9 ? "9+" : totalUnreadBadge}
                      </span>
                    )}
                  </Button>
                </DialogTrigger>
                <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="left-auto right-0 top-0 h-screen w-[88vw] max-w-[22rem] translate-x-0 translate-y-0 rounded-none border-l border-[rgba(125,95,52,0.18)] p-0 data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-left-0 data-[state=closed]:slide-out-to-left-0 data-[state=open]:slide-in-from-top-0 data-[state=closed]:slide-out-to-top-0" style={{ paddingTop: "var(--safe-area-top)" }}>
                  {MenuPanel}
                </DialogContent>
              </Dialog>
            )}
          </div>

          <div className="xl:hidden flex shrink-0 items-center justify-end -mr-1 overflow-visible">
              {showCartButton && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative h-8 w-8 overflow-visible text-white hover:bg-white/10 hover:text-white dark:text-foreground dark:hover:bg-accent"
                  onClick={handleCartClick}
                  aria-label={headerCartCount > 0 ? `Cart, ${headerCartCount} item${headerCartCount === 1 ? "" : "s"}` : "Cart"}
                >
                  <ShoppingCart className="h-5 w-5" />
                  {headerCartCount > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 z-10 inline-flex min-w-[1rem] h-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white shadow-[0_0_0_1.5px_hsl(15_55%_39%)] dark:shadow-[0_0_0_1.5px_hsl(var(--background))]">
                      {headerCartCount > 99 ? "99+" : headerCartCount}
                    </span>
                  )}
                </Button>
              )}

              <Button
                variant="ghost"
                size="icon"
                className="relative h-8 w-8 overflow-visible text-white hover:bg-white/10 hover:text-white dark:text-foreground dark:hover:bg-accent"
                aria-label={unreadNotificationCount > 0 ? `Notifications, ${unreadNotificationCount} unread` : "Notifications"}
                onClick={() => setLocation("/notifications")}
              >
                <Bell className="h-5 w-5" />
                {unreadNotificationCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 z-10 inline-flex min-w-[1rem] h-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white shadow-[0_0_0_1.5px_hsl(15_55%_39%)] dark:shadow-[0_0_0_1.5px_hsl(var(--background))]">
                    {unreadNotificationCount > 9 ? "9+" : unreadNotificationCount}
                  </span>
                )}
              </Button>

              <Button
                variant="ghost"
                size="icon"
                className="relative h-8 w-8 overflow-visible text-white hover:bg-white/10 hover:text-white dark:text-foreground dark:hover:bg-accent"
                aria-label="Open menu"
                onClick={() => setIsMenuOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>

              <Dialog open={isMenuOpen} onOpenChange={setIsMenuOpen}>
                <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="left-auto right-0 top-0 h-screen w-[88vw] max-w-[22rem] translate-x-0 translate-y-0 rounded-none border-l border-[rgba(125,95,52,0.18)] p-0 data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-left-0 data-[state=closed]:slide-out-to-left-0 data-[state=open]:slide-in-from-top-0 data-[state=closed]:slide-out-to-top-0" style={{ paddingTop: "var(--safe-area-top)" }}>
                  {MenuPanel}
                </DialogContent>
              </Dialog>
          </div>
        </div>

      </div>
      <ActiveOrderBanner />
      </header>

      {/* Everything below the sticky logo row scrolls away with the document on a
          normal page. A host that owns its own scroll container instead of the
          document (the swipe pager) slides it away by setting this variable. */}
      <div
        data-header-tail
        style={{ transform: "translate3d(0, calc(-1 * var(--stoop-header-tail-collapse, 0px)), 0)" }}
      >
      <div className="xl:hidden border-b border-primary/90 bg-brand-gradient-secondary text-white dark:bg-none dark:bg-background dark:text-foreground">
        <div className="container mx-auto px-4 min-w-0">
        <div
          className="overflow-hidden"
          style={{ height: `${mobileTopRowHeight}px` }}
        >
          <div className="flex w-full items-center gap-2 h-full">
            {isAuthenticated && allowedModes.length > 1 && (
              <div className="flex shrink-0 items-center rounded-full border border-white/20 bg-white/10 px-1 py-[1px]">
                <button
                  type="button"
                  onClick={() => handleModeChange("buyer")}
                  className={`rounded-full px-2.5 py-[2px] text-xs font-medium transition ${
                    currentMode === "buyer"
                      ? "bg-primary text-primary-foreground"
                      : "text-white/85"
                  }`}
                >
                  Buyer
                </button>
                <button
                  type="button"
                  onClick={() => handleModeChange("seller")}
                  className={`rounded-full px-2.5 py-[2px] text-xs font-medium transition ${
                    currentMode === "seller"
                      ? "bg-primary text-primary-foreground"
                      : "text-white/85"
                  }`}
                >
                  Sell
                </button>
              </div>
            )}

            {locationLabel !== undefined && (
              <Popover open={isLocationPopoverOpen} onOpenChange={setIsLocationPopoverOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-1 rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-xs font-medium text-white/90 hover:bg-white/20"
                    aria-label="Location settings"
                  >
                    <MapPin className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{locationLabel}</span>
                    <span className="ml-auto pl-1 text-[10px] opacity-60">▾</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-56 p-3">
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Your location</p>
                    <p className="text-sm font-semibold">{locationLabel}</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full justify-start gap-2 text-xs"
                      onClick={onUseCurrentLocation}
                      disabled={isLocating}
                    >
                      {isLocating ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Navigation className="h-3.5 w-3.5" />
                      )}
                      {isLocating ? "Locating..." : "Use current location"}
                    </Button>
                    {locationError && (
                      <p className="text-[11px] text-amber-600">{locationError}</p>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            )}

            {!isAuthenticated && (
              showMobileAuthTextButtons ? (
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2.5 text-white hover:bg-white/10 hover:text-white rounded-full text-xs font-medium"
                  asChild
                >
                  <Link href="/login">Login</Link>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2.5 text-white hover:bg-white/10 hover:text-white rounded-full text-xs font-medium"
                  asChild
                >
                  <Link href="/register">Register</Link>
                </Button>
              </div>
            ) : (
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 shrink-0 px-0 text-white hover:bg-white/10 hover:text-white justify-center rounded-full"
                  asChild
                >
                  <Link href="/login" aria-label="Login">
                    <User className="h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 shrink-0 px-0 text-white hover:bg-white/10 hover:text-white justify-center rounded-full"
                  asChild
                >
                  <Link href="/register" aria-label="Register">
                    <LogOut className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
      {currentMode === "seller" && <ComplianceAttestationBanner />}
      </div>
    </>
  );
}
